import React from "react"
import { observer } from "mobx-react"
import { Button, Menu } from "@mantine/core"
import { IconCheck, IconChevronDown, IconFileText, IconLink, IconRobot } from "@tabler/icons-react"
import { agentDirectionsFor, frameworkById } from "../../../docs/catalog"
import { prefetchAgentDirections } from "../../../docs/lazy"
import type { AgentStack } from "../../../docs/types"
import { useCopyAgentDirections } from "../../hooks/useCopyAgentDirections"
import type { CopyCtaLocation } from "../../../analytics"

type AgentDirectionsMenuProps = {
  stack: AgentStack
  framework: string
  ctaLocation: CopyCtaLocation
}

// The homepage cards' compact form of CopyAgentDirectionsButtons: the same two
// copies (the cover's link, or the full text) behind one "For agents" button,
// so a card's actions fit on one row beside Get started. The trigger says
// which one was copied for a moment, since the menu closes on the click. The
// full pair stays on every integration page.
export const AgentDirectionsMenu: React.FC<AgentDirectionsMenuProps> = observer(
  ({ stack, framework, ctaLocation }) => {
    const { copiedKind, copy, copyLink } = useCopyAgentDirections(framework, ctaLocation)
    const directions = agentDirectionsFor(stack)
    const copied = copiedKind(stack)
    const label = copied === "link" ? "Link copied" : copied === "directions" ? "Text copied" : "For agents"
    const name = frameworkById(framework)?.label ?? framework

    // Opening the menu fetches the text, so "Copy text" rarely waits for it.
    return (
      <Menu shadow="md" width={250} position="bottom-end" onOpen={() => prefetchAgentDirections(stack)}>
        <Menu.Target>
          <Button
            className="or-agent-menu-target"
            size="sm"
            variant="default"
            leftSection={copied ? <IconCheck size={16} /> : <IconRobot size={16} />}
            rightSection={copied ? null : <IconChevronDown size={14} />}
            aria-label={copied ? label : `Agent directions for ${name}`}
          >
            {label}
          </Button>
        </Menu.Target>
        <Menu.Dropdown>
          <Menu.Label>Agent directions</Menu.Label>
          <Menu.Item
            leftSection={<IconLink size={16} />}
            title={`Copy a link to the agent directions at ${directions.coverPath}`}
            onClick={() => void copyLink(stack)}
          >
            Copy link
          </Menu.Item>
          <Menu.Item
            leftSection={<IconFileText size={16} />}
            title="Copy the full agent directions as text"
            onClick={() => void copy(stack)}
          >
            Copy text
          </Menu.Item>
        </Menu.Dropdown>
      </Menu>
    )
  }
)
